import { useEffect, useRef, useState } from 'react'
import type { ConflictPolicy, StatEntry } from '../api'
import { formatBytes, formatDate } from '../format'
import type { PickedFile } from '../picked-files'

export interface Conflict {
  picked: PickedFile
  existing: StatEntry
}

export type ConflictChoice = ConflictPolicy | 'skip'

export interface ConflictResolution {
  picked: PickedFile
  choice: ConflictChoice
}

/**
 * Asks before anything is destroyed. Shown once per upload batch, walking the
 * conflicts one at a time unless the user opts to apply a choice to the rest.
 */
export default function ConflictDialog({
  conflicts,
  onResolve,
  onCancel,
}: {
  conflicts: Conflict[]
  onResolve: (resolutions: ConflictResolution[]) => void
  onCancel: () => void
}) {
  const [index, setIndex] = useState(0)
  const [applyToRest, setApplyToRest] = useState(false)
  const resolved = useRef<ConflictResolution[]>([])
  const dialog = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialog.current?.focus()
  }, [])

  const current = conflicts[index]
  if (!current) return null

  const remaining = conflicts.length - index - 1

  function choose(choice: ConflictChoice) {
    const rest = conflicts.slice(index)
    if (applyToRest) {
      resolved.current.push(...rest.map((conflict) => ({ picked: conflict.picked, choice })))
      onResolve(resolved.current)
      return
    }

    resolved.current.push({ picked: current!.picked, choice })
    if (index + 1 >= conflicts.length) {
      onResolve(resolved.current)
      return
    }
    setIndex(index + 1)
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conflict-title"
        tabIndex={-1}
        ref={dialog}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel()
        }}
      >
        <h2 id="conflict-title">이미 같은 이름의 파일이 있습니다</h2>
        <p className="mono conflict-name">{current.picked.path}</p>

        <dl className="facts">
          <div>
            <dt>기존 파일</dt>
            <dd>
              {formatBytes(current.existing.size)} · {formatDate(current.existing.mtime)}
            </dd>
          </div>
          <div>
            <dt>올리는 파일</dt>
            <dd>
              {formatBytes(current.picked.file.size)} ·{' '}
              {formatDate(current.picked.file.lastModified)}
            </dd>
          </div>
        </dl>

        <div className="modal-actions">
          <button type="button" className="danger" onClick={() => choose('overwrite')}>
            덮어쓰기
          </button>
          <button type="button" className="primary" onClick={() => choose('rename')}>
            이름 바꿔 저장
          </button>
          <button type="button" onClick={() => choose('skip')}>
            건너뛰기
          </button>
        </div>

        {remaining > 0 && (
          <label className="inline">
            <input
              type="checkbox"
              checked={applyToRest}
              onChange={(event) => setApplyToRest(event.target.checked)}
            />
            나머지 {remaining}건에도 모두 적용
          </label>
        )}

        <div className="modal-footer">
          <button type="button" className="link" onClick={onCancel}>
            업로드 전체 취소
          </button>
        </div>
      </div>
    </div>
  )
}
